import { Lock } from "lucide-react";
import React, { useEffect, useRef } from "react";

const Booth = ({
  boothId,
  boothNo,
  subtitle,
  boothType,
  title,
  size,
  x,
  y,
  width,
  height,
  color,
  borderRadius = 6,
  textColor = "#1a1a2e",
  fontSize = 14,
  showBottomSize = true,
  onClick,
  isReserved = false,
  activeTooltipId = null,
  setActiveTooltipId,
  // 👇 NEW
  reservedInfo = null,
  // {
  //   companyName: "ABC Corp",
  //   logo: "/logos/abc.png",
  //   url: "https://abccorp.com"
  // },
}) => {
  const boothGroupRef = useRef(null);


  const tooltipWidth = 160;
  const tooltipHeight = 70;
  const tooltipOffset = 12;
  const arrowHeight = 8;
  const showTooltipBelow = y <= tooltipHeight + tooltipOffset + arrowHeight;
  const tooltipX = x + width / 2 - tooltipWidth / 2;
  const tooltipY = showTooltipBelow
    ? y + height + tooltipOffset
    : y - tooltipHeight - tooltipOffset;
  const arrowBaseY = showTooltipBelow ? y + height + tooltipOffset : y - tooltipOffset;
  const arrowTipY = showTooltipBelow ? y + height + 4 : y - 4;

  const isPodcast = boothType === "podcast";
  const isTooltipVisible = activeTooltipId === boothId;

  useEffect(() => {
    if (!isTooltipVisible || !boothGroupRef.current?.parentNode) return;

    // SVG stacking follows DOM order, so move the active booth to the end.
    boothGroupRef.current.parentNode.appendChild(boothGroupRef.current);
  }, [isTooltipVisible]);

  return (
    <g
      ref={boothGroupRef}
      onMouseEnter={() => {
        setActiveTooltipId?.(boothId);
      }}
      onMouseLeave={() => {
        setActiveTooltipId?.((currentId) => (currentId === boothId ? null : currentId));
      }}
      onClick={() => {
        if (!isReserved && !isPodcast && onClick) {
          onClick({ boothId, boothNo, boothType, title, size });
        }
      }}
      style={{
        cursor: isPodcast ? "default" : "pointer",
      }}
    >
      {/* Shadow */}
      <rect
        x={x + 3}
        y={y + 3}
        width={width}
        height={height}
        rx={borderRadius}
        fill="rgba(0,0,0,0.1)"
      />

      {/* Booth body */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={borderRadius}
        fill={isTooltipVisible ? "#E91E8C" : color}
        stroke={isTooltipVisible ? "#c2185b" : "rgba(0,0,0,0.15)"}
        strokeWidth={isTooltipVisible ? 3 : 1.5}
        style={{ transition: "all 0.2s ease" }}
      />

      {/* Highlight */}
      <rect
        x={x + 2}
        y={y + 3}
        width={width - 4}
        height={height / 4}
        rx={borderRadius + 3}
        fill="rgba(255,255,255,0.2)"
      />

      {/* ===== RESERVED STATE ===== */}
      {isReserved && (
        <>
          {/* Dark overlay */}
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={borderRadius}
            fill="rgba(0,0,0,0.35)"
          />

          {/* Lock icon */}
          <Lock
            x={x + width / 2 - 8}
            y={y + height / 2 - 16}
            size={16}
            color="#fff"
          />

          {/* Reserved label */}
          <text
            x={x + width / 2}
            y={y + height / 2 + 14}
            textAnchor="middle"
            fontSize={fontSize - 2}
            fill="#fff"
            fontWeight="700"
          >
            RESERVED
          </text>
        </>
      )}

      {/* ===== ACTIVE BOOTH CONTENT ===== */}
      {!isReserved && (
        <>
          {/* Booth No (optional) */}
          {boothNo && (
            <text
              x={x + width / 2}
              y={y + 16}
              textAnchor="middle"
              fontSize={fontSize - 3}
              fill={isTooltipVisible ? "#fff" : "rgba(0,0,0,0.7)"}
              fontWeight="600"
            >
              Booth no: {boothNo}
            </text>
          )}


          {/* Title (supports line breaks) */}
          <text
            x={x + width / 2}
            y={y + height / 2.2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={fontSize}
            fill={isTooltipVisible ? "#fff" : textColor}
            fontWeight="700"
          >
            {String(title).split("\n").map((line, index) => (
              <tspan
                key={index}
                x={x + width / 2}
                dy={index === 0 ? "0em" : "1.1em"}
              >
                {line}
              </tspan>
            ))}
          </text>


          {/* Size */}
          {/* Size (optional) */}
          {size && showBottomSize && (
            <text
              x={x + width / 2}
              y={y + height - 10}
              textAnchor="middle"
              fontSize={fontSize - 3}
              fill={isTooltipVisible ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.6)"}
            >
              {size}
            </text>
          )}
          {/* subtitle (optional) */}
          {subtitle && (
            <text
              x={x + width / 2}
              y={y + height - 40}
              textAnchor="middle"
              fontSize={fontSize}
              fill="rgba(255,255,255,0.9)"
            >
              {subtitle}
            </text>
          )}

        </>
      )}

      {/* ===== TOOLTIP ===== */}
      {isTooltipVisible && !isReserved && isPodcast && (
        <g pointerEvents="none">
          <rect
            x={tooltipX}
            y={tooltipY}
            width={tooltipWidth}
            height={tooltipHeight}
            rx={8}
            fill="#fff"
            stroke="#e0e0e0"
            filter="drop-shadow(0 4px 6px rgba(0,0,0,0.15))"
          />
          <polygon
            points={
              showTooltipBelow
                ? `${x + width / 2 - 8},${arrowBaseY}
                   ${x + width / 2 + 8},${arrowBaseY}
                   ${x + width / 2},${arrowTipY}`
                : `${x + width / 2 - 8},${arrowBaseY}
                   ${x + width / 2 + 8},${arrowBaseY}
                   ${x + width / 2},${arrowTipY}`
            }
            fill="#fff"
          />
          <text
            x={x + width / 2}
            y={tooltipY + 18}
            textAnchor="middle"
            fontSize="12"
            fontWeight="700"
          >
            ProFX Media
          </text>
          <text
            x={x + width / 2}
            y={tooltipY + 32}
            textAnchor="middle"
            fontSize="11"
            fill="#666"
          >
            <tspan x={x + width / 2} dy="0">Podcast Included</tspan>
            <tspan x={x + width / 2} dy="1.3em">in package</tspan>
          </text>
        </g>
      )}

      {isTooltipVisible && !isReserved && !isPodcast && (
        <g pointerEvents="none">
          {/* Tooltip box */}
          <rect
            x={tooltipX}
            y={tooltipY}
            width={tooltipWidth}
            height={tooltipHeight}
            rx={8}
            fill="#fff"
            stroke="#e0e0e0"
            filter="drop-shadow(0 4px 6px rgba(0,0,0,0.15))"
          />

          {/* Arrow */}
          <polygon
            points={
              showTooltipBelow
                ? `${x + width / 2 - 8},${arrowBaseY}
                   ${x + width / 2 + 8},${arrowBaseY}
                   ${x + width / 2},${arrowTipY}`
                : `${x + width / 2 - 8},${arrowBaseY}
                   ${x + width / 2 + 8},${arrowBaseY}
                   ${x + width / 2},${arrowTipY}`
            }
            fill="#fff"
          />

          {/* Tooltip text */}
          {boothNo && (
            <text
              x={x + width / 2}
              y={tooltipY + 18}
              textAnchor="middle"
              fontSize="12"
              fontWeight="700"
            >
              Booth no: {boothNo}
            </text>
          )}

          <text
            x={x + width / 2}
            y={tooltipY + 34}
            textAnchor="middle"
            fontSize="12"
          >
            {title}
          </text>

          {size && (
            <text
              x={x + width / 2}
              y={tooltipY + 50}
              textAnchor="middle"
              fontSize="11"
              fill="#666"
            >
              Size: {size}
            </text>
          )}

        </g>
      )}

      {/* ===== RESERVED TOOLTIP ===== */}
      {isReserved && isTooltipVisible && reservedInfo && (
        <g>
          {/* Tooltip box */}
          <rect
            x={tooltipX}
            y={tooltipY}
            width={tooltipWidth}
            height={tooltipHeight}
            rx={8}
            fill="#fff"
            stroke="#e0e0e0"
            filter="drop-shadow(0 4px 6px rgba(0,0,0,0.15))"
          />

          {/* Arrow */}
          <polygon
            points={
              showTooltipBelow
                ? `${x + width / 2 - 8},${arrowBaseY}
                   ${x + width / 2 + 8},${arrowBaseY}
                   ${x + width / 2},${arrowTipY}`
                : `${x + width / 2 - 8},${arrowBaseY}
                   ${x + width / 2 + 8},${arrowBaseY}
                   ${x + width / 2},${arrowTipY}`
            }
            fill="#fff"
          />

          {/* Company logo */}
          {/* <image
            href={reservedInfo.logo}
            x={x + width / 2 - 55}
            y={y - 88}
            width={100}
            height={100}
            preserveAspectRatio="xMidYMid meet"
          /> */}
          <a
            href={reservedInfo.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <image
              href={reservedInfo.logo}
              x={x + width / 2 - 50}
              y={tooltipY + 6}
              width={100}
              height={42}
              preserveAspectRatio="xMidYMid meet"
              style={{ cursor: "pointer" }}
            />
          </a>


          {/* Company name */}
          <text
            x={x + width / 2}
            y={tooltipY + 60}
            fontSize="12"
            fontWeight="700"
            fill="#333"
            textAnchor="middle"
          >
            {reservedInfo.companyName}
          </text>

        </g>
      )}

    </g>
  );
};

export default Booth;
